import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import useDeviceDetect from 'utils/useDeviceDetect';

// antd component
import { Input } from 'antd';

// icons
import CustomIcons from 'components/CustomIcons';
import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';

function SearchButton({
  uniqueKey,
  value,
  loading,
  onSearch,
  onClick,
  onClose,
  // switch state
  show,
  setShow,
  // default props
  className
}) {
  const childClassNames = classNames('extra-bar-item', 'search-extra', className);

  const { isMobile } = useDeviceDetect();

  const searchRef = useRef(null);

  const [currentValue, setCurrentValue] = useState(value || '');
  const [timer, setTimer] = useState(null)

  useEffect(() => {
    return () => {
      timer && clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    if (currentValue.length > 0 && (show && (loading || !loading))) {
      searchRef?.current?.focus({
        cursor: 'end',
      });
    }
  }, [loading, show]);

  const onClickIcon = useCallback((e) => {
    !!setShow && setShow(prev => !prev);
    searchRef.current.focus({
      cursor: 'start',
    });
    !!onClick && onClick(e);
  }, [onClick, searchRef]);

  const onCloseSearch = useCallback((e) => {
    setShow !== undefined && setShow(prev => !prev);
    setCurrentValue('');
    onClose !== undefined && onClose(e);
  }, [onClose, setCurrentValue]);

  const onSearchCallback = useCallback((e) => {
    const searchBy = e.target.value;
    const delay = !isMobile ? 1500 : 2500;
    setCurrentValue(searchBy);
    // start parent action with delay (for loading actions etc.)
    if (onSearch !== undefined) {
      clearTimeout(timer);
      const newTimer = setTimeout(() => {
        onSearch(searchBy);
      }, delay);
      setTimer(newTimer)
    }
  }, [onSearch, setCurrentValue]);

  const render = useMemo(() => (
    <>
      {
        <CustomIcons
          key="search-icon"
          className={`search-extra__icon ${(!show || currentValue?.length > 0) ? 'show' : ''
            } ${loading ? 'disabled' : ''}`}
          onClick={onClickIcon}
          type="search"
        />
      }
      <span className={`search-extra__bar ${show ? 'show' : ''}`}>
        <Input 
          key="search-extra"
          name="search-extra"
          size="large"
          placeholder="Recherche..."
          prefix={<CustomIcons type="search" />}
          disabled={loading}
          suffix={
            <>
              {loading && <LoadingOutlined key="search-loading" />}
              {!loading && <CloseOutlined key="search-close" onClick={onCloseSearch} />}
            </>
          }
          onChange={onSearchCallback}
          value={currentValue}
          ref={searchRef}
        />
      </span>
    </>
  ), [currentValue, value, searchRef, show, loading, isMobile, onCloseSearch, onSearchCallback]);

  return (
    <div
      key={`search-by-list-${uniqueKey}`}
      className={childClassNames}
    >
      {render}
    </div>
  );
};

SearchButton.defaultProps = {
  uniqueKey: 'simple',
  show: false,
  loading: false
};
SearchButton.propTypes = {
  uniqueKey: PropTypes.string,
  onSearch: PropTypes.func,
  onClick: PropTypes.func,
  onClose: PropTypes.func,
  setShow: PropTypes.func,
  show: PropTypes.bool,
};

export default SearchButton;